import React from 'react';
import { LayoutGrid, LayoutGridItem } from '../../../../../layout/LayoutGrid';
import { Box, CircularProgress } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { GridColumns } from '@mui/x-data-grid-pro';
import TimelineIcon from '@mui/icons-material/Timeline';
import { XGrid } from 'app/components/Common/XGrid/XGrid';
import { useDispatch, useSelector } from 'react-redux';
import { XGridDefaultColumn } from 'app/components/Common/XGrid/cells/XGridDefaults';
import { xGridDateSort } from 'app/components/Common/XGrid/sorting/XGridSorting';
import { XGridChangeHistoryOverflowCell } from 'app/components/Common/XGrid/cells/XGridChangeHistoryOverflowCell';
import { XGridHistoricalHoverView } from 'app/components/Common/XGrid/cells/HoverViewRenderers/XGridHistoricalHoverView';
import { candidateDetailsSelection, selectChangeHistory } from '../../Profile/CandidateDetails.selector';
import { candidateDetailActions } from '../../Profile/CandidateDetails.redux';
import { XGridSSNClickableCell } from './ChangeHistoryClickableCell';
import { usePromiseTracker } from 'react-promise-tracker';
import { useTranslation } from 'react-i18next';
import { ChangeHistoryTimelineModal } from '@AMIEWEB/Common/ChangeHistoryTimeline';
import type { IXGridBulkActionButton } from '@AMIEWEB/Common/XGrid/Toolbar/XGridToolbar';

const useStyles = makeStyles()({
  container: {
    padding: '24px 0px',
  },
  MuiCircularProgressRoot: {
    left: '50%',
    position: 'absolute',
    top: '60vh',
  },
});

export const ChangeHistoryTab = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const candidateDetails = useSelector(candidateDetailsSelection);
  const changeHistory = useSelector(selectChangeHistory);
  const [timelineModalOpen, setTimelineModalOpen] = React.useState(false);

  const [sortModel, setSortModel] = React.useState<any>([
    {
      field: 'created',
      sort: 'desc',
    },
  ]);

  const { promiseInProgress } = usePromiseTracker({ area: 'get-candidate-details', delay: 0 });

  const rows = changeHistory?.rows || [];

  const fetchChangeHistory = React.useCallback(() => {
    if (candidateDetails?.travelerId && candidateDetails?.candidateGuid) {
      dispatch(
        candidateDetailActions.getChangeHistory({
          candidateId: candidateDetails.travelerId,
          candidateGuid: candidateDetails.candidateGuid,
        }),
      );
    }
  }, [candidateDetails?.travelerId, candidateDetails?.candidateGuid, dispatch]);

  React.useEffect(() => {
    fetchChangeHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateDetails?.travelerId]);

  const rightActions: IXGridBulkActionButton[] = React.useMemo(() => {
    if (!rows?.length) return [];
    return [
      {
        KeyId: 'timeline-visualization',
        Icon: TimelineIcon,
        tooltipProps: {
          tooltipContent: t('candidate.changeHistoryTab.timelineVisualization'),
          disabled: false,
        },
        onClick: () => setTimelineModalOpen(true),
      },
    ];
  }, [rows?.length, t]);

  const columns: GridColumns = [
    {
      ...XGridDefaultColumn,
      field: 'fieldChanged',
      headerName: 'Field',
    },
    {
      ...XGridDefaultColumn,
      field: 'oldValue',
      headerName: 'Old Value',
      renderCell: XGridChangeHistoryOverflowCell(XGridHistoricalHoverView)(XGridSSNClickableCell),
    },
    {
      ...XGridDefaultColumn,
      field: 'newValue',
      headerName: 'New Value',
      renderCell: XGridChangeHistoryOverflowCell(XGridHistoricalHoverView)(XGridSSNClickableCell),
    },
    {
      ...XGridDefaultColumn,
      field: 'userName',
      headerName: 'Changed By',
    },
    {
      ...XGridDefaultColumn,
      field: 'created',
      headerName: 'Date',
      type: 'date',
      sortComparator: xGridDateSort,
    },
    {
      ...XGridDefaultColumn,
      field: 'externalSystem',
      headerName: 'Source System',
    },
  ];

  const CandidateChangeHistoryGrid = React.useMemo(() => {
    return (
      <XGrid
        loading={promiseInProgress || changeHistory?.loading}
        columns={columns}
        rows={rows}
        headerHeight={45}
        autoHeight
        disableSelectionOnClick
        sortModel={sortModel}
        onSortModelChange={model => setSortModel(model)}
        toolbarProps={{
          refreshButton: true,
          refreshGrid: fetchChangeHistory,
          rightActions,
        }}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeHistory?.rows, columns, rows, rightActions, fetchChangeHistory]);

  return (
    <LayoutGrid>
      <LayoutGridItem xs={12}>
        <Box className={classes.container}>
          {CandidateChangeHistoryGrid}
          <ChangeHistoryTimelineModal
            open={timelineModalOpen}
            onClose={() => setTimelineModalOpen(false)}
            title={t('candidate.changeHistoryTab.timelineModalTitle')}
            historyData={rows}
          />
        </Box>
      </LayoutGridItem>
    </LayoutGrid>
  );
};
